import { useState, useCallback } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import {
  GENERATE_WIZARD_STEPS,
  DISPLAY,
  DISPLAY_SOURCE_PLATFORM,
  DISPLAY_SOURCE_BYO,
  DEFAULT_LANGUAGE,
  WIZARD_TEMPLATES,
  BYO_TEMPLATE_ID,
} from "../constants.js";
import { ArrowRightIcon } from "../Components/Icons";
import { createInitialBannerDoc } from "../lib/display/doc.js";
import StepBody, { ReviewStep } from "./WizardSteps";
import "./Wizard.css";

const REVIEW_KEY = "review";
const IMPLEMENTED_KINDS = [
  "cards",
  "url",
  "display_source",
  "display_sizes",
  "display_template",
  "offer",
];

function storageKey(brandSlug) {
  return `generate-wizard:${brandSlug}`;
}

function initialState() {
  return {
    offerId: null,
    landingUrl: "",
    landingUrlPrefilledFrom: null,
    displaySource: DISPLAY_SOURCE_PLATFORM,
    backgroundRefId: null,
    backgroundUrl: null,
    displayLanguage: DEFAULT_LANGUAGE,
    displaySizes: [...DISPLAY.CORE_SET_DEFAULT],
    // Template choice removed from the flow (little value across three,
    // similar options) - always defaults to custom-image now. See the
    // `steps` filter below, which skips the display_template step
    // entirely to match.
    displayTemplateId: "custom-image",
  };
}

function isValidUrl(value) {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

export default function Wizard({
  brand,
  identity,
  references,
  offers,
  credits,
  appConfig,
  onSaveOffers,
  onCreateAsset,
  isCreatingAsset,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Local copy, not just the prop — a newly-created offer needs to appear
  // immediately without waiting for a full config refetch, same as the
  // original's onOffersChange updating its own local list.
  const [localOffers, setLocalOffers] = useState(offers ?? []);
  const [createError, setCreateError] = useState(null);

  const [state, setState] = useState(() => {
    try {
      const stored = sessionStorage.getItem(storageKey(brand.slug));
      return stored ? { ...initialState(), ...JSON.parse(stored) } : initialState();
    } catch {
      return initialState();
    }
  });

  const byo = state.displaySource === DISPLAY_SOURCE_BYO;
  // display_template is always skipped now - the template choice was
  // removed from the flow entirely (defaults to custom-image, set in
  // initialState above). Was previously only skipped for BYO; kept as
  // its own line rather than folded into IMPLEMENTED_KINDS/elsewhere so
  // reverting this is a one-line change: swap back to
  // `!(s.key === "display_template" && byo)` and restore a real default
  // (e.g. null) in initialState.
  const steps = GENERATE_WIZARD_STEPS.filter((s) => s.key !== "display_template");
  const stepKeys = [...steps.map((s) => s.key), REVIEW_KEY];

  const stepKey = searchParams.get("step") || stepKeys[0];
  const stepIndex = Math.max(0, stepKeys.indexOf(stepKey));
  const stepConfig = steps.find((s) => s.key === stepKey);

  const update = useCallback(
    (patch) => {
      setState((current) => {
        const next = { ...current, ...patch };
        try {
          sessionStorage.setItem(storageKey(brand.slug), JSON.stringify(next));
        } catch {
          // storage full/blocked — wizard still works within this page,
          // it just won't survive a refresh.
        }
        return next;
      });
    },
    [brand.slug]
  );

  function goTo(key) {
    setSearchParams({ brand: brand.slug, step: key });
  }

  function stepComplete(key) {
    switch (key) {
      case "offer":
        return state.offerId !== null;
      case "landing_url":
        return isValidUrl(state.landingUrl);
      case "display_language":
        return state.displayLanguage !== "";
      case "display_sizes":
        return state.displaySizes.length > 0;
      case "display_template": {
        const template = WIZARD_TEMPLATES.find((t) => t.id === state.displayTemplateId);
        if (!template) return false;
        return state.displaySizes.some((s) => template.supportedSizes.includes(s));
      }
      default:
        return true;
    }
  }

  const canCreate = stepKeys.slice(0, -1).every(stepComplete);
  const creditCost = DISPLAY.CREDITS.BANNER_SET;
  const enoughCredits = credits >= creditCost;

  async function handleCreate() {
    if (!canCreate || !enoughCredits || isCreatingAsset) return;
    setCreateError(null);

    const templateId = byo ? BYO_TEMPLATE_ID : state.displayTemplateId;
    const template = WIZARD_TEMPLATES.find((t) => t.id === templateId);
    const offer = localOffers.find((o) => o.id === state.offerId);

    const recipe = {
      offerId: state.offerId,
      landingUrl: state.landingUrl,
      displaySource: state.displaySource,
      backgroundRefId: state.backgroundRefId,
      displayLanguage: state.displayLanguage,
      displaySizes: state.displaySizes,
      displayTemplateId: templateId,
      // The wizard no longer asks for these — sensible defaults keep the
      // recipe valid, same as the original.
      objective: "sales",
      angle: "benefit_led",
    };

    const content = createInitialBannerDoc({
      template,
      sizes: state.displaySizes,
      language: state.displayLanguage,
      identity,
      references,
      offerTitle: offer?.title ?? "",
      clickUrl: state.landingUrl,
    });

    // Bring-your-own-image: use the uploaded creative as the background and
    // bake all text into it by default, matching the original.
    if (byo && state.backgroundRefId) {
      content.slots.imageSource = { kind: "reference", id: state.backgroundRefId };
      content.slots.bakedText = ["headline", "subline", "cta"];
    }

    try {
      const asset = await onCreateAsset(brand.id, recipe, content);
      try {
        sessionStorage.removeItem(storageKey(brand.slug));
      } catch {
        // non-fatal — worst case the next visit resumes stale wizard state
      }
      navigate(`/compose/${asset.id}?brand=${brand.slug}`);
    } catch (err) {
      setCreateError("Couldn't create this asset. Please try again.");
    }
  }

  return (
    <div className="wizard">
      <div className="wizard__header">
        <h1 className="wizard__title">New banners for {brand.name}</h1>
        <span className="wizard__step-count">
          Step {stepIndex + 1} of {stepKeys.length}
        </span>
      </div>

      <div className="wizard__progress" aria-hidden>
        {stepKeys.map((key, i) => (
          <div
            key={key}
            className={`wizard__progress-bar${
              i < stepIndex
                ? " wizard__progress-bar--done"
                : i === stepIndex
                  ? " wizard__progress-bar--active"
                  : ""
            }`}
          />
        ))}
      </div>

      <div className="wizard__body">
        {stepKey === REVIEW_KEY ? (
          <ReviewStep
            state={state}
            offers={localOffers}
            credits={credits}
            creditCost={creditCost}
            enoughCredits={enoughCredits}
            canCreate={canCreate}
            creating={isCreatingAsset}
            createError={createError}
            onCreate={handleCreate}
            onJump={goTo}
          />
        ) : stepConfig ? (
          <div>
            <h2 className="wizard__step-title">{stepConfig.title}</h2>
            <p className="wizard__step-subtitle">{stepConfig.subtitle}</p>
            <div className="wizard__step-content">
              {IMPLEMENTED_KINDS.includes(stepConfig.kind) ? (
                <StepBody
                  config={stepConfig}
                  state={state}
                  onUpdate={update}
                  brand={brand}
                  appConfig={appConfig}
                  offers={localOffers}
                  onSaveOffers={onSaveOffers}
                  onOfferCreated={(offer) => setLocalOffers((prev) => [offer, ...prev])}
                />
              ) : (
                <p className="wizard__placeholder-note">
                  Step content for &ldquo;{stepConfig.kind}&rdquo; comes in the next pass —
                  navigation and validation are already wired up.
                </p>
              )}
            </div>
          </div>
        ) : null}
      </div>

      {stepKey !== REVIEW_KEY ? (
        <div className="wizard__nav">
          {stepIndex > 0 ? (
            <button
              type="button"
              onClick={() => goTo(stepKeys[stepIndex - 1])}
              className="wizard__back-btn"
            >
              Back
            </button>
          ) : (
            <Link to={`/brands/${brand.slug}`} className="wizard__back-btn">
              Cancel
            </Link>
          )}
          <button
            type="button"
            disabled={!stepComplete(stepKey)}
            onClick={() => goTo(stepKeys[stepIndex + 1])}
            className="btn btn--primary wizard__next-btn"
          >
            {stepIndex === stepKeys.length - 2 ? "Review brief" : "Next"}
            <ArrowRightIcon className="wizard__icon" />
          </button>
        </div>
      ) : null}
    </div>
  );
}